/**
 * Guest chat controller — handles unauthenticated chat via /api/demo/chat.
 *
 * Calls the API directly (no WebSocket, no pod) and parses OpenAI-format
 * SSE streaming responses. Tracks message count in sessionStorage.
 */

const API_BASE = "https://api.alitagpt.com";
const GUEST_MAX_MESSAGES = 50;
const STORAGE_KEY = "alita-guest-chat";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface GuestMessage {
  role: "user" | "assistant";
  content: string;
  timestamp: number;
}

interface GuestState {
  messages: GuestMessage[];
  count: number;
}

// ---------------------------------------------------------------------------
// Persistence (sessionStorage)
// ---------------------------------------------------------------------------

function loadGuestState(): GuestState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return { messages: [], count: 0 };
}

function saveGuestState(state: GuestState) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch { /* ignore */ }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function getGuestMessageCount(): number {
  return loadGuestState().count;
}

export function isGuestLimitReached(): boolean {
  return loadGuestState().count >= GUEST_MAX_MESSAGES;
}

export function getGuestMessages(): GuestMessage[] {
  return loadGuestState().messages;
}

/**
 * Send a guest chat message. Streams the response and updates
 * chatStream / chatMessages on the app state.
 */
export async function handleGuestChatSend(
  state: {
    chatStream: string | null;
    chatStreamStartedAt: number | null;
    chatMessages: unknown[];
    chatSending: boolean;
    chatMessage: string;
    lastError: string | null;
    requestUpdate?: () => void;
  },
  text: string,
): Promise<void> {
  const guest = loadGuestState();

  // Check limit
  if (guest.count >= GUEST_MAX_MESSAGES) {
    state.lastError = null;
    state.requestUpdate?.();
    return;
  }

  // Add user message to history
  const userMsg: GuestMessage = { role: "user", content: text, timestamp: Date.now() };
  guest.messages.push(userMsg);
  guest.count++;
  saveGuestState(guest);

  // Update UI — add user message to chat
  state.chatMessages = [
    ...state.chatMessages,
    { role: "user", content: [{ type: "text", text }], timestamp: Date.now() },
  ];
  state.chatMessage = "";
  state.chatSending = true;
  state.chatStream = "";
  state.chatStreamStartedAt = Date.now();
  state.requestUpdate?.();

  try {
    // Build messages array for API (only last 5)
    const apiMessages = guest.messages.slice(-GUEST_MAX_MESSAGES).map((m) => ({
      role: m.role,
      content: m.content,
    }));

    const resp = await fetch(`${API_BASE}/api/demo/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ messages: apiMessages }),
    });

    if (!resp.ok) {
      const err = await resp.json().catch(() => ({ error: "Request failed" })) as { error?: string; message?: string };
      const errorMsg = (err as { message?: string }).message || (err as { error?: string }).error || "Something went wrong";

      if (resp.status === 429) {
        // Rate limit — show as assistant message with CTA
        state.chatMessages = [
          ...state.chatMessages,
          {
            role: "assistant",
            content: [{ type: "text", text: `✨ ${errorMsg}` }],
            timestamp: Date.now(),
            isGuestLimit: true,
          },
        ];
      } else {
        state.chatMessages = [
          ...state.chatMessages,
          {
            role: "assistant",
            content: [{ type: "text", text: `⚠️ ${errorMsg}` }],
            timestamp: Date.now(),
            isError: true,
          },
        ];
      }
      state.chatStream = null;
      state.chatSending = false;
      state.chatStreamStartedAt = null;
      state.requestUpdate?.();
      return;
    }

    // Parse SSE stream
    if (resp.body) {
      await parseSSEStream(resp.body, state, guest);
    }
  } catch (err) {
    state.chatMessages = [
      ...state.chatMessages,
      {
        role: "assistant",
        content: [{ type: "text", text: "⚠️ Something went wrong. Please try again." }],
        timestamp: Date.now(),
        isError: true,
      },
    ];
    state.chatStream = null;
    state.chatSending = false;
    state.chatStreamStartedAt = null;
    state.requestUpdate?.();
  }
}

// ---------------------------------------------------------------------------
// SSE Parser — processes OpenAI-format streaming events
// ---------------------------------------------------------------------------

async function parseSSEStream(
  body: ReadableStream<Uint8Array>,
  state: {
    chatStream: string | null;
    chatStreamStartedAt: number | null;
    chatMessages: unknown[];
    chatSending: boolean;
    requestUpdate?: () => void;
  },
  guest: GuestState,
) {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let accumulated = "";
  let buffer = "";
  let rafId: number | null = null;

  const flushToState = () => {
    rafId = null;
    state.chatStream = accumulated;
    state.requestUpdate?.();
  };

  const scheduleFlush = () => {
    if (rafId === null) {
      rafId = requestAnimationFrame(flushToState);
    }
  };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const data = line.slice(6).trim();
        if (data === "[DONE]") continue;

        try {
          const parsed = JSON.parse(data) as {
            choices?: Array<{ delta?: { content?: string } }>;
          };
          const content = parsed.choices?.[0]?.delta?.content;
          if (content) {
            accumulated += content;
            scheduleFlush();
          }
        } catch {
          // Skip malformed chunks
        }
      }
    }
  } finally {
    if (rafId !== null) cancelAnimationFrame(rafId);
    reader.releaseLock();
  }

  // Final: add assistant message to history
  if (accumulated.trim()) {
    guest.messages.push({
      role: "assistant",
      content: accumulated,
      timestamp: Date.now(),
    });
    saveGuestState(guest);

    state.chatMessages = [
      ...state.chatMessages,
      {
        role: "assistant",
        content: [{ type: "text", text: accumulated }],
        timestamp: Date.now(),
      },
    ];
  }

  state.chatStream = null;
  state.chatSending = false;
  state.chatStreamStartedAt = null;
  state.requestUpdate?.();
}
